import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { format } from 'date-fns';
import { Calendar, FileText, User, Tag, Hash, Clock, Download, Eye, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { DocumentPreviewDialog } from '@/components/documents/DocumentPreviewDialog';

interface JournalEntry {
  id: string;
  record_number: string;
  record_type: string;
  record_date: string;
  record_title: string;
  transaction_description?: string;
  activity_description?: string;
  activity_category?: string;
  contributor_name?: string;
  contribution_purpose?: string;
  authorized_by?: string;
  status: string;
  created_at: string;
  parties_involved?: any[];
  internal_notes?: string;
  workflow_task_id?: string;
}

interface Document {
  id: string;
  document_name: string;
  document_type: string;
  status: string;
  file_url: string | null;
  signed_file_url: string | null;
  generated_at: string | null;
}

interface JournalEntryDetailModalProps {
  entry: JournalEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const recordTypeColors: Record<string, string> = {
  'Private Contribution Received': 'bg-green-100 text-green-800',
  'Grant or Distribution Made': 'bg-blue-100 text-blue-800',
  'Expense Payment': 'bg-red-100 text-red-800',
  'Activity Log Entry': 'bg-purple-100 text-purple-800',
  'General Record': 'bg-gray-100 text-gray-800',
  'Member Appointment': 'bg-yellow-100 text-yellow-800',
  'Officer Appointment': 'bg-orange-100 text-orange-800',
  'Asset Contribution': 'bg-teal-100 text-teal-800',
  'Resolution Adopted': 'bg-indigo-100 text-indigo-800',
  'Meeting Conducted': 'bg-pink-100 text-pink-800',
};

export const JournalEntryDetailModal: React.FC<JournalEntryDetailModalProps> = ({
  entry,
  open,
  onOpenChange,
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  useEffect(() => {
    if (open && entry?.workflow_task_id) {
      loadDocuments();
    } else if (!open) {
      setDocuments([]);
    }
  }, [open, entry?.workflow_task_id]);

  const loadDocuments = async () => {
    if (!entry?.workflow_task_id) return;
    
    setIsLoading(true);
    try {
      const { data: docs, error } = await supabase
        .from('generated_documents')
        .select('id, document_name, document_type, status, file_url, signed_file_url, generated_at')
        .eq('task_id', entry.workflow_task_id)
        .order('generated_at', { ascending: true });

      if (error) throw error;
      setDocuments(docs || []);
    } catch (error) {
      console.error('Error loading journal entry documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDocumentUrl = (doc: Document) => {
    return doc.signed_file_url || doc.file_url;
  };

  const handleDownload = async (doc: Document) => {
    const url = getDocumentUrl(doc);
    if (!url) return;
    
    setDownloadingDocId(doc.id);
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to fetch document');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const filename = `${doc.document_name?.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'document'}.pdf`;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      window.open(url, '_blank');
    } finally {
      setDownloadingDocId(null);
    }
  };

  if (!entry) return null;

  const typeColor = recordTypeColors[entry.record_type] || 'bg-gray-100 text-gray-800';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="pr-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-xl pr-4">{entry.record_title}</DialogTitle>
                <DialogDescription className="flex items-center gap-2 mt-2">
                  <Hash className="h-4 w-4" />
                  {entry.record_number}
                </DialogDescription>
              </div>
              <Badge className={`${typeColor} shrink-0`}>{entry.record_type}</Badge>
            </div>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Key Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="flex items-start sm:items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                  <span className="text-muted-foreground">Record Date:</span>
                  <span className="font-medium">
                    {format(new Date(entry.record_date), 'MMMM d, yyyy')}
                  </span>
                </div>
              </div>
              <div className="flex items-start sm:items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                  <span className="text-muted-foreground">Created:</span>
                  <span className="font-medium">
                    {format(new Date(entry.created_at), 'MMM d, yyyy h:mm a')}
                  </span>
                </div>
              </div>
              {entry.activity_category && (
                <div className="flex items-start sm:items-center gap-2 text-sm">
                  <Tag className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                    <span className="text-muted-foreground">Category:</span>
                    <Badge variant="outline">{entry.activity_category}</Badge>
                  </div>
                </div>
              )}
              {entry.authorized_by && (
                <div className="flex items-start sm:items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                    <span className="text-muted-foreground">Authorized By:</span>
                    <span className="font-medium">{entry.authorized_by}</span>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Description */}
            {(entry.transaction_description || entry.activity_description) && (
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Description
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {entry.transaction_description || entry.activity_description}
                </p>
              </div>
            )}

            {/* Contributor Info (for contributions) */}
            {entry.contributor_name && (
              <div>
                <h4 className="font-semibold mb-2">Contributor</h4>
                <p className="text-sm">{entry.contributor_name}</p>
                {entry.contribution_purpose && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Purpose: {entry.contribution_purpose}
                  </p>
                )}
              </div>
            )}

            {/* Parties Involved */}
            {entry.parties_involved && entry.parties_involved.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">Parties Involved</h4>
                <div className="space-y-2">
                  {entry.parties_involved.map((party: any, index: number) => {
                    const partyName = party.member_name || party.name || 'Unknown';
                    const partyRole = party.member_role || party.role;
                    const partyPosition = party.position;
                    
                    return (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>{partyName}</span>
                        {partyRole && (
                          <Badge variant="outline" className="text-xs">
                            {partyRole}
                          </Badge>
                        )}
                        {partyPosition && (
                          <Badge variant="outline" className="text-xs">
                            {partyPosition}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Internal Notes */}
            {entry.internal_notes && (
              <div>
                <h4 className="font-semibold mb-2">Notes</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {entry.internal_notes}
                </p>
              </div>
            )}

            {/* Associated Documents */}
            {entry.workflow_task_id && (
              <>
                <Separator />
                <div>
                  <h4 className="font-semibold flex items-center gap-2 mb-3">
                    <FileText className="h-4 w-4 text-primary" />
                    Associated Documents ({isLoading ? '...' : documents.length})
                  </h4>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : documents.length > 0 ? (
                    <div className="space-y-3">
                      {documents.map((doc) => (
                        <Card key={doc.id} className="overflow-hidden">
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <span className="font-medium truncate text-sm">{doc.document_name}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPreviewDocId(doc.id)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {getDocumentUrl(doc) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDownload(doc)}
                                    disabled={downloadingDocId === doc.id}
                                  >
                                    {downloadingDocId === doc.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Download className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No documents associated with this entry</p>
                  )}
                </div>
              </>
            )}

            {/* Status */}
            <div className="flex items-center justify-between pt-4 border-t">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge variant={entry.status === 'final' ? 'default' : 'secondary'}>
                {entry.status === 'final' ? 'Finalized' : entry.status}
              </Badge>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <DocumentPreviewDialog
        documentId={previewDocId}
        open={!!previewDocId}
        onClose={() => setPreviewDocId(null)}
      />
    </>
  );
};